import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  effect,
  inject,
  input,
  OnDestroy,
} from '@angular/core';
import { Table } from 'primeng/table';

@Component({
  selector: 'app-table-scroll-wrapper',
  templateUrl: './table-scroll-wrapper.component.html',
  styleUrl: './table-scroll-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableScrollWrapperComponent
  implements AfterContentInit, OnDestroy
{
  @ContentChild(Table, { descendants: true }) private tableRef?: Table;

  loading = input(false);
  maxHeightPx = input<number | null>(1000);
  scrollStepPx = input(300);
  rightButtonGapPx = input(2);
  buttonTopOffsetPx = input(108);
  buttonBottomOffsetPx = input(80);
  actionsColumnSelector = input('.actions-column');
  scrollContainerSelector = input(
    '.p-datatable-table-container, .p-datatable-wrapper',
  );
  leftButtonAriaLabel = input('Przewiń tabelę w lewo');
  rightButtonAriaLabel = input('Przewiń tabelę w prawo');

  protected horizontalScrollVisible = true;
  protected scrollOnVeryLeft = false;
  protected scrollOnVeryRight = false;
  protected rightScrollButtonOffsetPx = 8;

  readonly #cdr = inject(ChangeDetectorRef);

  #scrollElement?: HTMLElement;
  #refreshTimeout?: ReturnType<typeof setTimeout>;
  #actionsColumnResizeObserver?: ResizeObserver;
  readonly #scrollListener = (): void => this.#updateScrollState();

  constructor() {
    effect(() => {
      this.loading();
      this.maxHeightPx();
      this.#scheduleRefresh();
    });
  }

  ngAfterContentInit(): void {
    this.#scheduleRefresh();
  }

  ngOnDestroy(): void {
    if (this.#refreshTimeout) {
      clearTimeout(this.#refreshTimeout);
    }

    this.#scrollElement?.removeEventListener('scroll', this.#scrollListener);
    this.#actionsColumnResizeObserver?.disconnect();
  }

  protected scrollLeft(): void {
    this.#scrollElement?.scrollBy({
      left: -this.scrollStepPx(),
      behavior: 'smooth',
    });
  }

  protected scrollRight(): void {
    this.#scrollElement?.scrollBy({
      left: this.scrollStepPx(),
      behavior: 'smooth',
    });
  }

  #scheduleRefresh(): void {
    if (this.#refreshTimeout) {
      clearTimeout(this.#refreshTimeout);
    }

    this.#refreshTimeout = setTimeout(() => {
      this.#refreshTimeout = undefined;
      this.#initializeScrollControls();
    });
  }

  #initializeScrollControls(): void {
    const scrollElement = this.#getScrollElement();

    if (!scrollElement) {
      this.horizontalScrollVisible = false;
      this.scrollOnVeryLeft = true;
      this.scrollOnVeryRight = true;
      this.#cdr.markForCheck();
      return;
    }

    if (this.#scrollElement !== scrollElement) {
      this.#scrollElement?.removeEventListener('scroll', this.#scrollListener);
      this.#scrollElement = scrollElement;
      this.#scrollElement.addEventListener('scroll', this.#scrollListener);
    }

    this.#updateScrollState();
    this.#updateActionsColumnOffset();
    this.#observeActionsColumnWidth();
  }

  #updateScrollState(): void {
    const el = this.#scrollElement;

    if (!el) {
      this.horizontalScrollVisible = false;
      this.scrollOnVeryLeft = true;
      this.scrollOnVeryRight = true;
      this.#cdr.markForCheck();
      return;
    }

    this.horizontalScrollVisible = el.scrollWidth > el.clientWidth;
    this.scrollOnVeryLeft = el.scrollLeft <= 0;
    this.scrollOnVeryRight =
      el.scrollLeft + el.clientWidth >= el.scrollWidth - 5;

    this.#cdr.markForCheck();
  }

  #updateActionsColumnOffset(): void {
    const actionsColumn = this.#getTableElement()?.querySelector<HTMLElement>(
      this.actionsColumnSelector(),
    );

    this.rightScrollButtonOffsetPx =
      (actionsColumn?.offsetWidth ?? 0) + this.rightButtonGapPx();
    this.#cdr.markForCheck();
  }

  #observeActionsColumnWidth(): void {
    const actionsColumn = this.#getTableElement()?.querySelector<HTMLElement>(
      this.actionsColumnSelector(),
    );

    this.#actionsColumnResizeObserver?.disconnect();

    if (!actionsColumn) {
      return;
    }

    this.#actionsColumnResizeObserver = new ResizeObserver(() => {
      this.#updateActionsColumnOffset();
    });
    this.#actionsColumnResizeObserver.observe(actionsColumn);
  }

  #getScrollElement(): HTMLElement | null {
    return (
      this.#getTableElement()?.querySelector<HTMLElement>(
        this.scrollContainerSelector(),
      ) ?? null
    );
  }

  #getTableElement(): HTMLElement | null {
    return this.tableRef?.el.nativeElement ?? null;
  }
}
